import { ProtoAssembly, ProtoFace, ProtoPart, ProtoWire } from './engine.types';

/**
 * Hand-rolled decoder for `assets/sample_meshes/assembly.proto`.
 *
 * Replaces `protobuf.load(...)` + reflection decoding, which cost us three
 * things on the editor's critical path:
 *
 *  1. **A network round-trip per decode.** `protobuf.load()` fetches and
 *     re-parses the .proto *text* every time it is called, and it was called
 *     once per assembly — base, organic, and parametric — plus in the jaws and
 *     compare paths. None of it was cached.
 *  2. **Reflection decoding.** protobufjs's generic decoder is materially
 *     slower than a schema-specific reader.
 *  3. **An entire extra copy of every mesh.** Reflection yields `number[]` for
 *     `repeated double`, which the engine then converted with
 *     `new Float32Array(face.vertices)`. On a large assembly that is millions
 *     of boxed doubles allocated and immediately thrown away.
 *
 * This reader emits `Float32Array` / `Uint32Array` directly — the exact types
 * `BufferAttribute` wants — so the geometry path does zero conversion.
 *
 * Precision note: vertices are `double` on the wire but are truncated to
 * float32 here. That is not a regression — the previous code did the same
 * truncation one step later, because Three.js uploads float32 to the GPU
 * regardless. (Halving the wire format itself is a separate, cross-component
 * change: the runner writes these files.)
 *
 * The schema is tiny and frozen:
 *
 *   Wire     { repeated double geometry = 1 [packed]; }
 *   Face     { string id = 1; string name = 2;
 *              repeated double vertices = 3 [packed];
 *              repeated int32  indices  = 4 [packed]; }
 *   Part     { string name = 1; string id = 2; string pid = 3; string color = 4;
 *              repeated Wire wires = 5; repeated Face faces = 6; }
 *   Assembly { string id = 1; repeated Part parts = 2; }
 *
 * Both packed and unpacked encodings of the repeated scalar fields are
 * accepted: proto3 defaults to packed, but that is an encoder choice and the
 * producer here is the runner, not protobufjs.
 */

const WIRETYPE_VARINT = 0;
const WIRETYPE_FIXED64 = 1;
const WIRETYPE_LENGTH_DELIMITED = 2;
const WIRETYPE_FIXED32 = 5;

const EMPTY_F32 = new Float32Array(0);
const EMPTY_U32 = new Uint32Array(0);

class Reader {
  readonly view: DataView;
  pos = 0;

  constructor(readonly bytes: Uint8Array) {
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }

  get eof(): boolean {
    return this.pos >= this.bytes.length;
  }

  /** Reads a base-128 varint. Values above 2^53 are not expected in this schema. */
  varint(): number {
    let result = 0;
    let shift = 0;
    for (;;) {
      if (this.pos >= this.bytes.length) {
        throw new Error('assembly proto: truncated varint');
      }
      const byte = this.bytes[this.pos++];
      result += shift < 28 ? (byte & 0x7f) << shift : (byte & 0x7f) * Math.pow(2, shift);
      if ((byte & 0x80) === 0) return result;
      shift += 7;
      if (shift > 63) throw new Error('assembly proto: varint too long');
    }
  }

  /** Length-delimited payload as a subrange view (no copy). */
  bytesField(): Uint8Array {
    const len = this.varint();
    const end = this.pos + len;
    if (end > this.bytes.length) throw new Error('assembly proto: length-delimited field overruns buffer');
    const out = this.bytes.subarray(this.pos, end);
    this.pos = end;
    return out;
  }

  string(): string {
    return utf8Decode(this.bytesField());
  }

  /** Skips one field of the given wire type, for forward compatibility. */
  skip(wireType: number): void {
    switch (wireType) {
      case WIRETYPE_VARINT:
        this.varint();
        return;
      case WIRETYPE_FIXED64:
        this.pos += 8;
        return;
      case WIRETYPE_LENGTH_DELIMITED: {
        // Read the length into a local first. `this.pos += this.varint()` would
        // evaluate `this.pos` *before* varint() advances it, silently dropping
        // the length prefix from the skip and desyncing the reader.
        const len = this.varint();
        this.pos += len;
        return;
      }
      case WIRETYPE_FIXED32:
        this.pos += 4;
        return;
      default:
        throw new Error(`assembly proto: unsupported wire type ${wireType}`);
    }
  }
}

const textDecoder = typeof TextDecoder !== 'undefined' ? new TextDecoder('utf-8') : null;

function utf8Decode(bytes: Uint8Array): string {
  if (textDecoder) return textDecoder.decode(bytes);
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += String.fromCharCode(bytes[i]);
  return out;
}

/**
 * Reads a packed `double` block straight into a Float32Array.
 *
 * This is the hot path: element count is known up front from the byte length,
 * so we allocate exactly once and never build an intermediate array.
 */
function packedDoublesToF32(block: Uint8Array): Float32Array {
  const count = block.byteLength >>> 3;
  const out = new Float32Array(count);
  const view = new DataView(block.buffer, block.byteOffset, block.byteLength);
  for (let i = 0; i < count; i++) {
    out[i] = view.getFloat64(i << 3, true);
  }
  return out;
}

/** Counts varints in a packed block: one per byte whose continuation bit is clear. */
function countVarints(block: Uint8Array): number {
  let n = 0;
  for (let i = 0; i < block.length; i++) {
    if ((block[i] & 0x80) === 0) n++;
  }
  return n;
}

function packedVarintsToU32(block: Uint8Array): Uint32Array {
  const out = new Uint32Array(countVarints(block));
  const reader = new Reader(block);
  let i = 0;
  while (!reader.eof) out[i++] = reader.varint();
  return out;
}

/**
 * Concatenates typed-array chunks, used only on the unpacked-encoding fallback
 * path. The packed path — what every current producer emits — never allocates
 * these.
 */
function concatF32(chunks: Float32Array[]): Float32Array {
  if (chunks.length === 0) return EMPTY_F32;
  if (chunks.length === 1) return chunks[0];
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Float32Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

function concatU32(chunks: Uint32Array[]): Uint32Array {
  if (chunks.length === 0) return EMPTY_U32;
  if (chunks.length === 1) return chunks[0];
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Uint32Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

function decodeWire(bytes: Uint8Array): ProtoWire {
  const reader = new Reader(bytes);
  const chunks: Float32Array[] = [];
  while (!reader.eof) {
    const tag = reader.varint();
    const field = tag >>> 3;
    const wireType = tag & 7;
    if (field === 1) {
      if (wireType === WIRETYPE_LENGTH_DELIMITED) {
        chunks.push(packedDoublesToF32(reader.bytesField()));
        continue;
      }
      if (wireType === WIRETYPE_FIXED64) {
        const single = new Float32Array(1);
        single[0] = reader.view.getFloat64(reader.pos, true);
        reader.pos += 8;
        chunks.push(single);
        continue;
      }
    }
    reader.skip(wireType);
  }
  return { geometry: concatF32(chunks) };
}

function decodeFace(bytes: Uint8Array): ProtoFace {
  const reader = new Reader(bytes);
  let id = '';
  let name = '';
  const vertexChunks: Float32Array[] = [];
  const indexChunks: Uint32Array[] = [];

  while (!reader.eof) {
    const tag = reader.varint();
    const field = tag >>> 3;
    const wireType = tag & 7;

    switch (field) {
      case 1:
        if (wireType === WIRETYPE_LENGTH_DELIMITED) { id = reader.string(); continue; }
        break;
      case 2:
        if (wireType === WIRETYPE_LENGTH_DELIMITED) { name = reader.string(); continue; }
        break;
      case 3:
        if (wireType === WIRETYPE_LENGTH_DELIMITED) {
          vertexChunks.push(packedDoublesToF32(reader.bytesField()));
          continue;
        }
        if (wireType === WIRETYPE_FIXED64) {
          const single = new Float32Array(1);
          single[0] = reader.view.getFloat64(reader.pos, true);
          reader.pos += 8;
          vertexChunks.push(single);
          continue;
        }
        break;
      case 4:
        if (wireType === WIRETYPE_LENGTH_DELIMITED) {
          indexChunks.push(packedVarintsToU32(reader.bytesField()));
          continue;
        }
        if (wireType === WIRETYPE_VARINT) {
          indexChunks.push(Uint32Array.of(reader.varint()));
          continue;
        }
        break;
    }
    reader.skip(wireType);
  }

  return {
    id,
    name,
    vertices: concatF32(vertexChunks),
    indices: concatU32(indexChunks)
  };
}

function decodePart(bytes: Uint8Array): ProtoPart {
  const reader = new Reader(bytes);
  let name = '';
  let id = '';
  let pid = '';
  let color = '';
  const wires: ProtoWire[] = [];
  const faces: ProtoFace[] = [];

  while (!reader.eof) {
    const tag = reader.varint();
    const field = tag >>> 3;
    const wireType = tag & 7;

    if (wireType === WIRETYPE_LENGTH_DELIMITED) {
      switch (field) {
        case 1: name = reader.string(); continue;
        case 2: id = reader.string(); continue;
        case 3: pid = reader.string(); continue;
        case 4: color = reader.string(); continue;
        case 5: wires.push(decodeWire(reader.bytesField())); continue;
        case 6: faces.push(decodeFace(reader.bytesField())); continue;
      }
    }
    reader.skip(wireType);
  }

  return { name, id, pid, color, wires, faces };
}

/**
 * Decodes an `Assembly` message.
 *
 * Synchronous by design — there is no schema to fetch, so callers that used to
 * await `protobuf.load()` no longer need to.
 */
export function decodeAssembly(buffer: ArrayBuffer | Uint8Array): ProtoAssembly {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const reader = new Reader(bytes);
  let id = '';
  const parts: ProtoPart[] = [];

  while (!reader.eof) {
    const tag = reader.varint();
    const field = tag >>> 3;
    const wireType = tag & 7;

    if (wireType === WIRETYPE_LENGTH_DELIMITED) {
      if (field === 1) { id = reader.string(); continue; }
      if (field === 2) { parts.push(decodePart(reader.bytesField())); continue; }
    }
    reader.skip(wireType);
  }

  return { id, parts };
}
